import React, { useState, useRef, useEffect } from 'react';
import { 
  Input, 
  Button, 
  Box,
  Text,
  useColorModeValue,
  Center,
  HStack,
  IconButton,
  Collapse,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const GuessInput = ({ onSubmitGuess, gameActive, category, currentWord, onHintPenalty }) => {
  const [guess, setGuess] = useState('');
  const [showWordArea, setShowWordArea] = useState(false);
  const [correctPositions, setCorrectPositions] = useState(new Set());
  const [hasGuessed, setHasGuessed] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const cancelRef = useRef();
  const inputRef = useRef(null);
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  // Focus input when component mounts or when game becomes active
  useEffect(() => {
    if (gameActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [gameActive]);

  const handleHintClick = () => {
    setShowConfirmDialog(true);
  };

  const handleHintConfirm = () => {
    const penalty = hasGuessed ? 100 : 50;
    onHintPenalty(penalty);
    setShowWordArea(true);
    setShowConfirmDialog(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!guess.trim() || !gameActive) return;
    
    const currentGuess = guess.trim();
    setHasGuessed(true);
    
    // Update correct positions
    const newCorrectPositions = new Set(correctPositions);
    currentGuess.split('').forEach((letter, index) => {
      if (letter === currentWord[index]) {
        newCorrectPositions.add(index);
      }
    });
    setCorrectPositions(newCorrectPositions);
    
    onSubmitGuess(currentGuess);
    setGuess('');
    setShowWordArea(false); // Hide hint after guess
  };

  const renderWordArea = () => {
    if (!currentWord) return null;

    return (
      <HStack spacing={2} justify="center" minH="2.5rem">
        {currentWord.split('').map((letter, index) => {
          const isCorrect = correctPositions.has(index);
          
          let color = 'gray.300';
          
          if (isCorrect) {
            color = 'green.500';
          }

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color={color}
                textTransform="uppercase"
                minW="1.5rem"
                textAlign="center"
              >
                {letter === ' ' ? ' ' : (isCorrect ? letter : '_')}
              </Text>
            </motion.div>
          );
        })}
      </HStack>
    );
  };

  return (
    <Center>
      <Box width="100%" maxW="400px">
        <Text fontWeight="medium" color="gray.600" fontSize="sm" textAlign="center" mb={2}>
          {category ? `Category: ${category.charAt(0).toUpperCase() + category.slice(1)}` : 'What do you see?'}
        </Text>
        
        {/* Toggle Button */}
        <Box textAlign="center" mb={2}>
          <HStack spacing={1} justify="center">
            <Tooltip label={`${hasGuessed ? '100' : '50'} point penalty`}>
              <Text fontSize="sm" color="gray.600">
                Hint
              </Text>
            </Tooltip>
            <IconButton
              aria-label="Toggle word area"
              icon={showWordArea ? <ChevronUpIcon /> : <ChevronDownIcon />}
              onClick={handleHintClick}
              variant="ghost"
              size="sm"
            />
          </HStack>
        </Box>

        {/* Word Area */}
        <Collapse in={showWordArea}>
          <Box 
            mb={4} 
            p={4} 
            bg={useColorModeValue('gray.50', 'gray.700')} 
            borderRadius="md"
            boxShadow="sm"
          >
            {renderWordArea()}
          </Box>
        </Collapse>

        {/* Input Form */}
        <Box as="form" onSubmit={handleSubmit}>
          <Input
            ref={inputRef}
            placeholder="Enter your guess..."
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            isDisabled={!gameActive}
            size="lg"
            bg={inputBg}
            borderColor={inputBorder}
            _hover={{ borderColor: useColorModeValue('gray.300', 'gray.500') }}
            _focus={{
              borderColor: useColorModeValue('blue.500', 'blue.300'),
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
            _disabled={{
              opacity: 0.6,
              cursor: 'not-allowed',
            }}
          />
        </Box>

        {/* Confirmation Dialog */}
        <AlertDialog
          isOpen={showConfirmDialog}
          leastDestructiveRef={cancelRef}
          onClose={() => setShowConfirmDialog(false)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Hint Penalty
              </AlertDialogHeader>

              <AlertDialogBody>
                Showing the hint will cost you {hasGuessed ? '100' : '50'} points. Do you want to continue?
              </AlertDialogBody>

              <AlertDialogFooter justifyContent="flex-start">
                <Button ref={cancelRef} onClick={() => setShowConfirmDialog(false)}>
                  Cancel
                </Button>
                <Button colorScheme="blue" onClick={handleHintConfirm} ml={3}>
                  Show Hint
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    </Center>
  );
};

export default GuessInput;